import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import CatalogPage from './pages/CatalogPage';
import ForumPage from './pages/ForumPage';
import AboutPage from './pages/AboutPage';
import AddArchive from './pages/AddArchive';
import ProductsList from './pages/ProductsList';
import ProductPage from './pages/ProductPage';
import DetailsList from './pages/DetailsList';
import DetailPage from './pages/DetailPage';
import PhotoPage from './pages/PhotoPage';
import LiterPage from './pages/LiterPage';
import GuessYearPage from './pages/GuessYearPage';
import YearDetailsPage from './pages/YearDetailsPage';
import SearchPage from './pages/SearchPage';
import EditDetailPage from './pages/EditDetailPage';
import ManufacturerListPage from './pages/ManufacturerList';
import ManufacturerPage from './pages/ManufacturerPage';
import ModificationsPage from './pages/ModificationsPage';
import EditProductPage from './pages/EditProductPage';
import GalleryPage from './pages/GalleryPage';
import RegisterPage from './pages/RegisterPage';
import LegalPage from './pages/LegalPage';
import ModerationPage from './pages/ModerationPage';
import DetailTypesPage from './pages/DetailTypesPage';
import ExpPage from './pages/ExpPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ArticlePage from './pages/ArticlePage';
import ArticlesPage from './pages/ArticlesPage';
import ArticleEditPage from './pages/ArticleEditPage';
import SokolovIdentifierPage from './pages/SokolovIdentifier';
import Header from './components/Header';
import Footer from './components/Footer';
import RequestStatusBar from './components/RequestStatusBar';
import ScrollManager from './components/ScrollManager';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider } from './contexts/AuthContext';
import { RequestStatusProvider } from './contexts/RequestStatusContext';

const YANDEX_METRIKA_ID = 103243779;

function MetrikaPageTracker() {
  const { pathname, search, hash } = useLocation();
  const previousUrlRef = useRef(
    typeof window === 'undefined' ? null : window.location.href,
  );

  useEffect(() => {
    const currentUrl = window.location.href;
    const previousUrl = previousUrlRef.current;

    // The counter initialization already records the first page load.
    if (!previousUrl || currentUrl === previousUrl) {
      previousUrlRef.current = currentUrl;
      return;
    }

    if (typeof window.ym === 'function') {
      window.ym(YANDEX_METRIKA_ID, 'hit', currentUrl, {
        title: document.title,
        referer: previousUrl,
      });
    }

    previousUrlRef.current = currentUrl;
  }, [pathname, search, hash]);

  return null;
}

function AppRoutes() {
  return (
    <div className="site-frame">
      <ScrollManager />
      <MetrikaPageTracker />
      <Header />
      <div className="site-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/liter" element={<LiterPage />} />
          <Route path="/forum" element={<ForumPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/add" element={<AddArchive />} />
          <Route path="/products" element={<ProductsList />} />
          <Route path="/products/:id" element={<ProductPage />} />
          <Route path="/details" element={<DetailsList />} />
          <Route path="/details/:id" element={<DetailPage />} />
          <Route path="/details/:id/edit" element={<EditDetailPage />} />
          <Route path="/details/year/:year" element={<YearDetailsPage />} />
          <Route path="/products/:id/edit" element={<EditProductPage />} />
          <Route path="/photos/:id" element={<PhotoPage />} />
          <Route path="/year" element={<GuessYearPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/manufacturers" element={<ManufacturerListPage />} />
          <Route path="/manufacturers/:id" element={<ManufacturerPage />} />
          <Route path="/modifications" element={<ModificationsPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/legal/:documentKey" element={<LegalPage />} />
          <Route path="/moderation" element={<ModerationPage />} />
          <Route path="/types" element={<DetailTypesPage />} />
          <Route path="/exp" element={<ExpPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/:slug" element={<ArticlePage />} />
          <Route path="/admin/articles/new" element={<ArticleEditPage />} />
          <Route path="/admin/articles/:id/edit" element={<ArticleEditPage />} />
          <Route path="/tools/sokolov-identifier" element={<SokolovIdentifierPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <Footer />
      <RequestStatusBar />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <RequestStatusProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </RequestStatusProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
